<script type="text/javascript">
	import moment from 'moment';
	import mapboxgl from 'mapbox-gl';
	import { onMount, onDestroy } from "svelte";

	import MenuItem from "../FlyoutMenu/MenuItem.svelte";
	import LayerListSwitch from '../LayerList/LayerListSwitch.svelte';

	import MapStore from "../../Map/Store";
	import Vessel from "../../Models/Vessel";

	import MySvelteTable from "../AlertList/MySvelteTable.svelte";
	import { writable } from 'svelte/store';
	
	export let dataPoints = [];
	export let layerEnabled = writable(false);
	
	let map = MapStore.mapbox.instance;
	let mapInitialised = MapStore.meta.initialised;

	let tableRowData = [];
	let selectedPoint = null;

	let pointPopup = new mapboxgl.Popup({
		closeButton: false,
		closeOnClick: true
	});
	pointPopup.on("close", function(e) {
        selectedPoint = null;
    });

	let formatTime = (t) => {
		return Vessel.formatTime(new Date(t).toISOString().substr(0, 19))
	};
	let calcHours = (from ,to) => {
		from = moment(from).unix()
		to = moment(to).unix();
		return Math.floor((to - from) / 60.0 / 60.0); // number of hours
	};

	let minPointsEnabled = writable(false);
	let minPoints = writable(1);
	
	let minHoursEnabled = writable(false);
	let minHours = writable(1);

	let onCellClick = (event) => {
		const columnKey = event.detail.key;
		const rowData = event.detail.row;

		const imo = rowData.imo;
		const fromTimestamp = rowData.fromTimestamp;
		const toTimestamp = rowData.toTimestamp;

		// Find LineString by imo/from/to
		let result = null;
		let prevSelectedPoint = selectedPoint;
		selectedPoint = null;
		for(let i = 0; i < dataPoints.length; i++) {
			const point = dataPoints[i];
			if (point.properties.imo == imo && point.properties.startTime == fromTimestamp && point.properties.endTime == toTimestamp) {
				result = point;
				break;
			}
		}
		// console.log("result", result);
		// If it exists, focus on it!
		if (result) {
			const from = result.geometry.coordinates;
			MapStore.helpers.zoomToPoint(from);

			const fromTime = result.properties.startTime;
			const toTime = result.properties.endTime;
			const countPoints = result.properties.countPoints;
			const hrs = calcHours(fromTime, toTime);

			let nl = "<br/>";
			
			let toHtml = "";
			toHtml += "<b>From</b>: " + formatTime(fromTime) + " " + nl;
			toHtml += "<b>To</b>: " + formatTime(toTime) + " " + nl;
			toHtml += "<b>Hours:</b> " + hrs + " hrs" + nl;
			toHtml += "<b>Points:</b> " + countPoints + nl;
			pointPopup.setHTML(toHtml);
			pointPopup.setLngLat(from);
			pointPopup.addTo($map);

			selectedPoint = result;
		}
		if (selectedPoint !== null &&
			prevSelectedPoint !== null &&
			selectedPoint.properties.imo === prevSelectedPoint.properties.imo &&
			selectedPoint.properties.fromTimestamp === prevSelectedPoint.properties.fromTimestamp
			) {
				selectedPoint = null;
			pointPopup.remove();
		}

	};

	let tableColumns = [
		{
			key: "imo",
			title: "IMO",
			value: v => v.imo,
			sortable: true,
			renderValue: (v, v2) => {
				return v.imo;
			},
			class: v => {
				return v.selected ? "selected" : "";
			},
			headerClass: "imo"
		},
		{
			key: "fromTimestamp",
			title: "From",
			value: v => v.fromTimestamp,
			renderValue: v => "<span title='" + v.fromTimestamp + "'>" + formatTime(v.fromTimestamp) + "</span>",
			sortable: true,
			class: v => {
				return " " + (v.selected ? "selected" : "");
			},
		},
		{
			key: "length",
			title: "Length",
			value: v => v.lengthHours,
			renderValue: v => "<span title='" + formatTime(v.toTimestamp) + "'>" + v.lengthHours + " hrs</span>",
			sortable: true,
			class: v => {
				return " " + (v.selected ? "selected" : "");
			},
			headerClass: "imo"
		},
		{
			key: "countPoints",
			title: "Points",
			value: v => v.countPoints,
			renderValue: v => "<span title='" + v.countPoints + "'>" + v.countPoints + " pts</span>",
			sortable: true,
			class: v => {
				return " " + (v.selected ? "selected" : "");
			},
			headerClass: "imo"
		}
	];

	onMount(() => {

		let onParameterUpdate = () => {
			selectedPoint = null;
			pointPopup.remove();
			update(dataPoints, $minPoints, $minHours);
		};
		minHoursEnabled.subscribe(onParameterUpdate);
		minHours.subscribe(onParameterUpdate);

		minPointsEnabled.subscribe(onParameterUpdate);
		minPoints.subscribe(onParameterUpdate);
	});

	onDestroy(()=>{
		pointPopup.remove();
	});

	$: {
		layerEnabled;
		$layerEnabled;

		$minPoints;
		$minHours;

		selectedPoint;
		update(dataPoints, $minPoints, $minHours);
	}

	let layerChange = (event) => {
		const checked = event.target.checked;
		$layerEnabled = checked;
	};

	let update = (points, minPoints, minHours) => {
		if ($map && $mapInitialised && $layerEnabled) {
			if (Number.isNaN(parseInt(minPoints))) {
				return; 
			}
			if (Number.isNaN(parseFloat(minHours))) {
				return; 
			}
			minPoints = parseInt(minPoints);
			minHours = parseFloat(minHours);
			// console.log('update', points.length, minPoints, minHours);
			tableRowData = [];
			
			for (let i = 0; i < dataPoints.length; i++) {
				const count = dataPoints[i].properties.countPoints;
				if (count < minPoints) { 
					continue;
				}
				const hours = calcHours(dataPoints[i].properties.startTime, dataPoints[i].properties.endTime);
				if (hours < minHours) { 
					continue;
				}
				let row = {
					imo: dataPoints[i].properties.imo,
					fromTimestamp: dataPoints[i].properties.startTime,
					toTimestamp: dataPoints[i].properties.endTime,
					lengthHours: hours,
					countPoints: count,
					selected: selectedPoint !== null && dataPoints[i].properties.startTime === selectedPoint.properties.startTime
				};
				tableRowData.push(row);
			}
		
		}
	};
	export const updateData = () => {
		update(dataPoints, $minPoints, $minHours);
	}
	export const reset = () => {
		dataPoints = [];
		updateData();
	}



</script>

<style type="text/css">
	.investigate-list-component span {
	  font-size: 10px;
	}
	table tr td, span {
		font-size: 10px;
	}
	:global(.aisInputGapTF) {
	    width: 20px !important;
	    background-color: white !important;
	    height: 100% !important;
	    padding: 1px!important;
	    display: inline-block !important;
	    vertical-align: baseline !important;
	    font-size: 12px !important;
	    text-align: center !important;
	}

</style>

<MenuItem title={"Investigate Merged/Stationary Points"}>
	
	<div class='layer-list-component'>
		
		<LayerListSwitch
			checked="{$layerEnabled}"
			label={"Enabled"}
			on:change={layerChange}
		>
		</LayerListSwitch>

		{#if $layerEnabled}
			<div class="layer-list-item">
				<div class="lli-label">
					Minimum points (<input type="text" class="aisInputGapTF" rows="1" wrap="soft" bind:value={$minPoints} placeholder="number of "/> points)
				</div>
			</div>
			<div class="layer-list-item">
				<div class="lli-label">
					Minimum hours (<input type="text" class="aisInputGapTF" rows="1" wrap="soft" bind:value={$minHours} placeholder="number of "/> hrs)
				</div>
			</div>
		{/if}

	</div>

	{#if $layerEnabled}
		<div class="selection-list-element" style="clear:both;">
			<div class="alert-list-content mdc-elevation--z2 investigate-list-component" style="height:auto;">
				<MySvelteTable on:clickCell={onCellClick} rows={tableRowData} columns={tableColumns} />
			</div>
		</div>
	{/if}
</MenuItem>
